// Edit modal for an existing attendance row.
// Spec: docs/admin-attendance-create-edit-history.md §4
// Only checkinTime / checkoutTime / reason are mutable; user/shift/date are read-only.

import { useEffect, useState } from 'react';
import { Alert, DatePicker, Form, Input, Modal, notification } from 'antd';
import dayjs from 'dayjs';
import CustomButton from 'components/custom-button';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import {
  Attendance,
  AdminEditAttendanceDto,
} from 'types/attendance';
import { mapAttendanceError } from '../helpers/attendance-error';
import { isDateInWindow } from '../helpers/attendance-date-window';

interface Props {
  open: boolean;
  attendance: Attendance | null;
  onClose: () => void;
  onSuccess: () => void;
}

const EditAttendanceModal = ({ open, attendance, onClose, onSuccess }: Props) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Pre-fill form when modal opens with a row.
  useEffect(() => {
    if (!open || !attendance) return;
    form.setFieldsValue({
      checkinTime: attendance.checkinTime ? dayjs(attendance.checkinTime) : null,
      checkoutTime: attendance.checkoutTime ? dayjs(attendance.checkoutTime) : null,
      reason: '',
    });
  }, [open, attendance, form]);

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: any) => {
    if (!attendance) return;

    // Date-window guard against the existing row's date.
    if (!isDateInWindow(attendance.date)) {
      notification.warning({
        message: 'Bản ghi nằm ngoài phạm vi cho phép sửa (tháng hiện tại / tháng trước).',
      });
      return;
    }

    // Checkout is optional (user may have only checked in).
    // Only enforce strict ordering if both values are present.
    if (
      values.checkinTime &&
      values.checkoutTime &&
      !dayjs(values.checkoutTime).isAfter(dayjs(values.checkinTime))
    ) {
      notification.warning({ message: 'Giờ checkout phải sau giờ checkin.' });
      return;
    }

    // Send only fields that actually changed; reason is always required.
    // Compare via .valueOf() (epoch ms) to avoid false positives from TZ offset
    // formatting differences (BE may return `+07:00`, FE produces `Z`).
    const newCheckin = values.checkinTime ? dayjs(values.checkinTime).toISOString() : null;
    const newCheckout = values.checkoutTime ? dayjs(values.checkoutTime).toISOString() : null;
    const newCheckinMs = newCheckin ? dayjs(newCheckin).valueOf() : null;
    const newCheckoutMs = newCheckout ? dayjs(newCheckout).valueOf() : null;
    const oldCheckinMs = attendance.checkinTime ? dayjs(attendance.checkinTime).valueOf() : null;
    const oldCheckoutMs = attendance.checkoutTime ? dayjs(attendance.checkoutTime).valueOf() : null;

    const dto: AdminEditAttendanceDto = { reason: (values.reason || '').trim() };
    if (newCheckin && newCheckinMs !== oldCheckinMs) dto.checkinTime = newCheckin;
    if (newCheckout && newCheckoutMs !== oldCheckoutMs) dto.checkoutTime = newCheckout;

    if (!dto.checkinTime && !dto.checkoutTime) {
      notification.warning({
        message: 'Vui lòng thay đổi ít nhất giờ checkin hoặc checkout.',
      });
      return;
    }

    setLoading(true);
    try {
      await AxiosUtils.patch(`${API.attendanceAdmin}/${attendance.id}`, dto);
      notification.success({ message: 'Đã cập nhật bản ghi chấm công.' });
      onSuccess();
      handleClose();
    } catch (err) {
      notification.error({ message: mapAttendanceError(err) });
    } finally {
      setLoading(false);
    }
  };

  const userLabel = attendance?.user
    ? `${attendance.user.username || attendance.user.email || ''} (#${attendance.user.id})`
    : attendance
    ? `User #${(attendance as any).userId ?? ''}`
    : '';
  const shiftLabel = attendance?.shift?.name ?? '';
  const dateLabel = attendance?.date ?? '';

  const outOfWindow = !!attendance && !isDateInWindow(attendance.date);

  return (
    <Modal
      title="Sửa bản ghi chấm công"
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      {outOfWindow && (
        <Alert
          type="warning"
          showIcon
          message="Bản ghi nằm ngoài phạm vi cho phép sửa (chỉ tháng hiện tại / tháng trước)."
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        <Form.Item label="Nhân viên">
          <Input value={userLabel} disabled />
        </Form.Item>
        <Form.Item label="Ca làm">
          <Input value={shiftLabel} disabled />
        </Form.Item>
        <Form.Item label="Ngày">
          <Input value={dateLabel} disabled />
        </Form.Item>
        <Form.Item
          label="Giờ checkin"
          name="checkinTime"
          rules={[{ required: true, message: 'Vui lòng nhập giờ checkin.' }]}
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: '100%' }}
            placeholder="Chọn giờ checkin"
          />
        </Form.Item>
        <Form.Item
          label="Giờ checkout"
          name="checkoutTime"
          extra="Bỏ trống nếu user chưa checkout."
        >
          <DatePicker
            showTime
            format="YYYY-MM-DD HH:mm:ss"
            style={{ width: '100%' }}
            placeholder="Chọn giờ checkout (tuỳ chọn)"
            allowClear
          />
        </Form.Item>
        <Form.Item
          label="Lý do"
          name="reason"
          rules={[
            { required: true, message: 'Vui lòng nhập lý do.' },
            { min: 5, message: 'Lý do tối thiểu 5 ký tự.' },
            { max: 500, message: 'Lý do tối đa 500 ký tự.' },
          ]}
        >
          <Input.TextArea
            rows={3}
            maxLength={500}
            showCount
            placeholder="VD: Sửa do nhập sai giờ checkin"
          />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text="Lưu"
            bgColor="secondary-green"
            htmlType="submit"
            loading={loading}
            disabled={outOfWindow}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default EditAttendanceModal;
